import { useEffect } from 'react'
import { z } from 'zod'
import { reportError } from '@mc/monitoring'
import { usePromotion } from './usePromotion'

const UrlSchema = z.string().url()

export const PromoPercentagesSchema = z.object({
  standard: z.number().positive(),
  plus: z.number().positive(),
  premium: z.number().positive(),
})

const StyledFeatureVariableSchema = z.object({
  text: z.union([
    z.string(),
    z.array(
      z.object({
        style: z.string(),
        copy: z.string(),
      }),
    ),
  ]),
})

// The two formats getUrgencyVariableForDate supports, and nothing else. Date-only
// resolves against the visitor's start-of-day; the ISO-Z form resolves against an exact
// instant. `precision: 0` matches that helper's YYYY_MM_DD_T_HH_MM_SS_Z_REGEX literally —
// a fractional-seconds value like 2026-08-14T07:00:00.000Z would fail the regex and
// silently take the start-of-day branch instead.
const AvailableFromSchema = z.union([
  z.string().date(),
  z.string().datetime({ precision: 0 }),
])

const UrgencyFeatureVariableSchema = z.record(
  z.string(),
  z.object({
    available_from: AvailableFromSchema,
    text: z.string(),
    text_short: z.string(),
  }),
)

const TermsFeatureVariablesSchema = z.record(
  z.string(),
  z.object({
    available_from: AvailableFromSchema,
    text: z.string(),
    link: z.string().url(),
  }),
)

const PromoFeatureVariablesSchema = z.object({
  promo_key: z.string(),

  // Promo mechanic
  gift_eligible: z.boolean(),
  coupon_codes: z.object({
    standard: z.string().optional(),
    plus: z.string().optional(),
    premium: z.string().optional(),
  }),
  percentages: PromoPercentagesSchema,

  // Pages config
  home_page: z
    .object({
      hero_images: z.array(
        z.object({
          firstColumn: z.string().url(),
          secondColumn: z.string().url(),
        }),
      ),
    })
    .optional(),

  promo_page: z.object({
    hero_image: z.string().url().optional(),
    hero_title: StyledFeatureVariableSchema,
  }),

  gift_page: z
    .object({
      hero_image: z
        .string()
        .refine((value) => !UrlSchema.safeParse(value).success, {
          message:
            'gift_page.hero_image must be a gift card template slug (e.g. "thanks-dad"), not a URL. URLs are no longer supported: the gift hero is a stack of card templates, and this value is interpolated into a template filename.',
        })
        .optional(),
      hero_title: StyledFeatureVariableSchema.optional(),
      hero_subtitle: StyledFeatureVariableSchema.optional(),
      catalog_order: z.array(z.string()).optional(),
    })
    .optional(),

  // Copies
  discount_copy: StyledFeatureVariableSchema,
  banner_and_sticky_footer_title: StyledFeatureVariableSchema,
  modal_offer_slogan: StyledFeatureVariableSchema,
  quiz_sticky_footer_copy: StyledFeatureVariableSchema,
  discount_modal_promo_overriden_body: StyledFeatureVariableSchema,

  // Urgency
  offer_terms_teal: TermsFeatureVariablesSchema,
  urgency_texts: UrgencyFeatureVariableSchema,
  // Exact promo end instant, UTC. Optional because promos published before it existed, and any
  // promo saved without an end date, won't carry it — consumers must handle it being absent.
  promo_end_at: z.string().datetime().optional(),
})

export type PromoFeatureVariables = z.infer<typeof PromoFeatureVariablesSchema>

export const useParseFeatureVariables = () => {
  const { isPromotionOn, variables } = usePromotion()

  useEffect(() => {
    if (isPromotionOn) {
      parseFeatureVariables(variables)
    }
  }, [isPromotionOn, variables])
}

export const parseFeatureVariables = (variables: Record<string, unknown>) => {
  if (!variables) {
    reportError(
      'Error getting experiment variables while trying to validate tiers discount',
    )
    return null
  }

  const result = PromoFeatureVariablesSchema.safeParse(variables) || {}

  if (result.error) {
    // eslint-disable-next-line no-console
    console.error('Invalid promo variables', result.error?.format())
    reportError('Invalid promo variables', {
      err: JSON.stringify(result.error?.format()),
    })
    return null
  }

  return result.data
}
